import { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

import { ListOfActiveAttacks } from './ActiveAttacks/ActiveAttacks'

import toast from 'lib/toast'
import agent from 'agent'
import { pluralize, messageFromResponseError } from 'utils/strings'
import Button from 'components/common/Button'
import theme from 'theme'
import ConfirmationModal from 'components/common/ConfirmationModal'
import TextArea from 'components/common/TextArea'
import useAuth from 'hooks/use-auth'
import { track } from 'track'
import useAttackResource from 'hooks/use-attack-resource'
import Popover from 'components/common/Popover'
import CardTooltip from 'components/common/CardTooltip'
import Text from 'components/common/Text'
import InputSwitch from 'components/common/InputSwitch'

function HaltAllConfirmation({ open, onCancel, doFetch }) {
  const { currentTeamId } = useAuth()
  const [reason, setReason] = useState('')
  const [selectAll, setSelectAll] = useState(true)
  const [selectedRows, setSelectedRows] = useState({})

  return (
    <ConfirmationModal
      confirmButtonProps={{
        disabled: !selectAll && !Object.keys(selectedRows).length // Disable button if "Select All Attacks" toggle is off and no attacks are selected
      }}
      confirmText={`${
        isEmpty(selectedRows)
          ? 'Confirm'
          : `Halt ${Object.keys(selectedRows).length} ${pluralize(
              'Attack',
              Object.keys(selectedRows).length
            )}`
      }`}
      css={{ textAlign: 'left' }}
      message={
        <Fragment>
          {/* Our API accepts no attacks (similar to targeting) to select "ALL". This toggle is a UI-only device to reduce confusion of what attacks are selected - even though we don't send any up */}
          <InputSwitch
            data-test-halt-all-attacks-toggle
            checked={selectAll}
            css={{ justifyContent: 'center', marginBottom: theme.space[3] }}
            label="Select All Attacks"
            onChange={({ target: { checked } }) => {
              setSelectAll(checked)

              if (checked) {
                setSelectedRows({})
              }
            }}
          />
          <div>
            <Text
              as="h5"
              css={{
                marginBottom: theme.space[3],
                textAlign: 'left'
              }}
            >
              Currently running attacks
            </Text>
          </div>
          <div
            data-halt-all-active-attacks-list
            css={{
              border: theme.border.base,
              overflow: 'hidden',
              marginBottom: theme.space[5],
              borderRadius: theme.borderRadius[0],
              maxHeight: 220,
              overflowY: 'scroll'
            }}
          >
            <ListOfActiveAttacks
              clickableRows={false}
              selectableRows={!selectAll}
              selectedRows={selectedRows}
              showHaltButton={false}
              onRowSelected={opts => {
                if (selectedRows[opts.id]) {
                  const newSelectedRows = Object.assign({}, selectedRows)
                  delete newSelectedRows[opts.id]

                  setSelectedRows(newSelectedRows)
                } else {
                  setSelectedRows({ ...selectedRows, [opts.id]: opts })
                }
              }}
            />
          </div>
          <TextArea
            data-test-input="reason"
            name="reason"
            placeholder="Optional Reason"
            type="text"
            value={reason}
            onChange={({ target: { value } }) => setReason(value)}
          />
        </Fragment>
      }
      open={open}
      title="Halt All Attacks"
      onCancel={() => {
        setSelectedRows({})
        onCancel()
      }}
      onConfirm={async () => {
        const allAttacksSelected = isEmpty(selectedRows)
        try {
          if (allAttacksSelected) {
            await agent.post(`/halts?teamId=${currentTeamId}`, { reason })
          } else {
            Object.values(selectedRows).forEach(activeAttackHalt => {
              if (activeAttackHalt.isScenario) {
                activeAttackHalt.onHaltRequest({
                  reason,
                  runNumber: activeAttackHalt.runNumber
                })
              } else {
                activeAttackHalt.onHaltRequest({ reason })
              }
            })
          }

          const toastText = `${
            !allAttacksSelected ? Object.values(selectedRows).length : ''
          } ${pluralize(
            'Attack',
            Object.values(selectedRows).length
          )} successfully halted.`

          toast({
            text: toastText,
            type: 'success'
          })

          const trackingOptions = allAttacksSelected
            ? { attackIds: 'all' }
            : {
                attackIds: Object.keys(selectedRows)
              }

          track('HaltAllAttacks', trackingOptions)
        } catch (e) {
          toast({
            type: 'error',
            text: messageFromResponseError(
              e,
              'There was an issue halting all attacks. Please try again later.'
            )
          })
        }
        doFetch()
        onCancel()
        setSelectedRows({})
      }}
    />
  )
}

HaltAllConfirmation.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  doFetch: PropTypes.func
}

export default function HaltAllAttacksButton() {
  const [haltConfirmationOpen, setHaltConfirmationOpen] = useState(false)
  const {
    activeAttacksCount,
    doFetch,
    doFetchActiveScenarios
  } = useAttackResource({
    completed: false
  })

  // It happened that I opened the confirmation and few seconds after no attacks were active.
  // but the confirmation box stayed open
  useEffect(() => {
    if (activeAttacksCount === 0 && haltConfirmationOpen)
      setHaltConfirmationOpen(false)
  }, [activeAttacksCount, haltConfirmationOpen])

  return (
    <Fragment>
      <HaltAllConfirmation
        doFetch={() => {
          try {
            doFetch()
            doFetchActiveScenarios()
          } catch (e) {
            toast({
              type: 'error',
              text: messageFromResponseError(
                e,
                'There was an issue fetching all attacks. Please try again later.'
              )
            })
          }
        }}
        open={haltConfirmationOpen}
        onCancel={() => setHaltConfirmationOpen(false)}
      />
      <Popover>
        {({ getTargetProps, getPopoverProps }) => (
          <Fragment>
            <div {...getTargetProps()}>
              <Button
                key="halt"
                data-test-halt-all-button
                destructive
                css={{
                  whiteSpace: 'nowrap'
                }}
                disabled={activeAttacksCount === 0}
                onClick={() => setHaltConfirmationOpen(true)}
              >
                Halt All Attacks
              </Button>
            </div>
            {activeAttacksCount === 0 && (
              <CardTooltip
                displayArrow
                size="small"
                variation="dark"
                {...getPopoverProps()}
              >
                <Text as="small" color="white">
                  No active attacks
                </Text>
              </CardTooltip>
            )}
          </Fragment>
        )}
      </Popover>
    </Fragment>
  )
}

HaltAllAttacksButton.propTypes = {
  alfiStore: PropTypes.object,
  authenticationStore: PropTypes.object,
  children: PropTypes.node,
  confirmHaltAll: PropTypes.func,
  haltAllAttacks: PropTypes.func
}
